import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Panel, DarkBtn, ConfirmDelete, Field } from "@/components/admin/AdminUI";

const strip = ({ id, created_at, ...rest }) => rest;

export default function Categories() {
  const [cats, setCats] = useState([]);
  const [edit, setEdit] = useState(null);

  const load = () => api.get("/admin/categories").then(({ data }) => setCats(data));
  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { ...strip(edit), order: Number(edit.order) };
      if (edit.id) await api.put(`/admin/categories/${edit.id}`, body);
      else await api.post("/admin/categories", body);
      toast.success("Kategori kaydedildi");
      setEdit(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const update = async (c, patch) => {
    await api.put(`/admin/categories/${c.id}`, { ...strip(c), ...patch });
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= cats.length) return;
    const a = cats[idx];
    const b = cats[j];
    await Promise.all([update(a, { order: j }), update(b, { order: idx })]);
    load();
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/categories/${id}`);
      toast.success("Kategori silindi");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div data-testid="admin-categories-page">
      <PageHeader title="Kategoriler" desc="QR menüdeki sekmelerin sırası ve görünürlüğü.">
        <DarkBtn onClick={() => setEdit({ name: "", slug: "", description: "", order: cats.length, active: true })} data-testid="admin-category-add-btn"><Plus size={16} /> Yeni Kategori</DarkBtn>
      </PageHeader>
      <Panel className="divide-y divide-[#E5DCD0]">
        {cats.map((c, i) => (
          <div key={c.id} className="flex items-center gap-3 p-4" data-testid={`admin-category-row-${c.slug}`}>
            <div className="flex flex-col">
              <button onClick={() => move(i, -1)} className="p-1 hover:text-[#9E7B27]" aria-label="Yukarı" data-testid={`admin-category-up-${c.slug}`}><ArrowUp size={14} /></button>
              <button onClick={() => move(i, 1)} className="p-1 hover:text-[#9E7B27]" aria-label="Aşağı" data-testid={`admin-category-down-${c.slug}`}><ArrowDown size={14} /></button>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold">{c.name}</p>
              <p className="text-xs text-[#6B5E56] truncate">/{c.slug} · {c.description}</p>
            </div>
            <label className="flex items-center gap-2 text-xs text-[#6B5E56]">
              <Switch checked={c.active} onCheckedChange={async (v) => { await update(c, { active: v }); load(); }} data-testid={`admin-category-active-${c.slug}`} /> Aktif
            </label>
            <button onClick={() => setEdit(c)} className="p-2 rounded-md hover:bg-[#F3EDE2]" aria-label="Düzenle" data-testid={`admin-category-edit-${c.slug}`}><Pencil size={15} /></button>
            <ConfirmDelete onConfirm={() => remove(c.id)}>
              <button className="p-2 rounded-md hover:bg-red-50 text-[#C84B31]" aria-label="Sil" data-testid={`admin-category-delete-${c.slug}`}><Trash2 size={15} /></button>
            </ConfirmDelete>
          </div>
        ))}
      </Panel>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-serif-ym text-2xl">{edit?.id ? "Kategoriyi Düzenle" : "Yeni Kategori"}</DialogTitle></DialogHeader>
          {edit && (
            <form onSubmit={save} className="space-y-4" data-testid="category-form">
              <Field label="Ad *"><Input required value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="category-name-input" /></Field>
              <Field label="Bağlantı kısaltması" hint="Boş bırakılırsa otomatik oluşturulur."><Input value={edit.slug} onChange={(e) => setEdit({ ...edit, slug: e.target.value })} data-testid="category-slug-input" /></Field>
              <Field label="Açıklama"><Input value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} data-testid="category-description-input" /></Field>
              <label className="flex items-center gap-2 text-sm"><Switch checked={edit.active} onCheckedChange={(v) => setEdit({ ...edit, active: v })} data-testid="category-active-switch" /> Menüde göster</label>
              <DarkBtn type="submit" className="w-full" data-testid="category-save-btn">Kaydet</DarkBtn>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
